import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';

export default function Guests() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', address: '' });

  const fetchData = (q) => api.get('/guests', { params: q ? { search: q } : {} }).then(r => setGuests(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { fetchData(); }, []);

  const handleSearch = (val) => { setSearch(val); fetchData(val); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/guests/${editing.id}`, form);
      else await api.post('/guests', form);
      setDialogOpen(false); setEditing(null); setForm({ first_name: '', last_name: '', email: '', phone: '', address: '' }); fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (g) => {
    setEditing(g); setForm({ first_name: g.first_name, last_name: g.last_name, email: g.email || '', phone: g.phone || '', address: g.address || '' }); setDialogOpen(true);
  };

  const handleDelete = async (id) => { if (!window.confirm('Delete guest?')) return; await api.delete(`/guests/${id}`); fetchData(); };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="guests-page">
      <div className="page-header">
        <h1 className="page-title">Guests</h1>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search guests..." value={search} onChange={e => handleSearch(e.target.value)} className="pl-9 w-full sm:w-64" data-testid="guest-search-input" />
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm({ first_name: '', last_name: '', email: '', phone: '', address: '' }); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-guest-btn"><Plus className="w-4 h-4 mr-2" />Add Guest</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Guest' : 'Add Guest'}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>First Name</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required data-testid="guest-firstname-input" /></div>
                  <div><Label>Last Name</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required data-testid="guest-lastname-input" /></div>
                </div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="guest-email-input" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="guest-phone-input" /></div>
                <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="guest-address-input" /></div>
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="guest-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="table-wrap">
        <table className="w-full data-table min-w-[500px]">
          <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Actions</th></tr></thead>
          <tbody>
            {guests.map(g => (
              <tr key={g.id} data-testid={`guest-row-${g.id}`}>
                <td className="font-medium text-slate-900">{g.first_name} {g.last_name}</td>
                <td>{g.email || '-'}</td>
                <td>{g.phone || '-'}</td>
                <td>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(g)} data-testid={`edit-guest-${g.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(g.id)} data-testid={`delete-guest-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {guests.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400">No guests found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
